import React from "react";
import { Link } from "react-router-dom";
import authService from "../api-authorization/AuthorizeService";

import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Toolbar,
    Typography,
    TextField,
    InputAdornment,
    IconButton,
    Tooltip,
    Chip,
    LinearProgress,
    Divider,
    Stack,
    Skeleton,
    Switch,
    FormControlLabel,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

import { HelpText } from "../HelpText";
import SimpleBackdrop from "../backdrop";
import RandomQuote from "../randomQuote";
import FormDialog from "./formdialog";

// ---------- utils ----------
function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
}
function getComparator(order, orderBy) {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort(array, comparator) {
    const stabilized = array.map((el, index) => [el, index]);
    stabilized.sort((a, b) => {
        const cmp = comparator(a[0], b[0]);
        if (cmp !== 0) return cmp;
        return a[1] - b[1];
    });
    return stabilized.map((el) => el[0]);
}
const formatNumber = (n) => new Intl.NumberFormat().format(n);
const parseDateFromImageFilename = (filename) => {
    if (!filename || typeof filename !== "string") return 0;
    const m = filename.match(/^(\d{17})\./);
    if (!m) return 0;
    const raw = m[1];
    const year = Number(raw.slice(0, 4));
    const month = Number(raw.slice(4, 6));
    const day = Number(raw.slice(6, 8));
    const hour = Number(raw.slice(8, 10));
    const minute = Number(raw.slice(10, 12));
    const second = Number(raw.slice(12, 14));
    const ms = Number(raw.slice(14, 17));
    const ts = Date.UTC(year, month - 1, day, hour, minute, second, ms);
    return Number.isFinite(ts) ? ts : 0;
};

// ---------- main ----------
export default function Brands({ updateTitle }) {
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(25);
    const [items, setItems] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [brandLimit, setBrandLimit] = React.useState(1);

    const [query, setQuery] = React.useState("");
    const [order, setOrder] = React.useState("desc");
    const [orderBy, setOrderBy] = React.useState("last_updated");
    const [dense, setDense] = React.useState(false);

    const columns = [
        { id: "name", label: "Name", minWidth: 180, sortable: true },
        { id: "image_count", label: "Images", minWidth: 120, sortable: true, align: "right" },
        { id: "images_without_annotation", label: "Images Without Annotations", minWidth: 220, sortable: true, align: "right" },
        { id: "last_updated", label: "Last Updated", minWidth: 180, sortable: true },
        { id: "last_seen_date", label: "Last Seen", minWidth: 160, sortable: true },
    ];

    const usage = Math.min(100, Math.round((items.length / Math.max(brandLimit || 1, 1)) * 100));

    const fetchData = React.useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch("/api/organisation/brands");
            authService.followIfLoginRedirect(response);
            const data = await response.json();
            setItems(Array.isArray(data.brands) ? data.brands : []);
            setBrandLimit(data.brandLimit ?? 1);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, []);

    const refresh = React.useCallback(() => {
        fetchData().catch(console.error);
    }, [fetchData]);

    React.useEffect(() => {
        updateTitle?.("Brands");
        fetchData().catch(console.error);
    }, [fetchData, updateTitle]);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleChangePage = (_e, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const handleSearch = (e) => {
        setQuery(e.target.value);
        setPage(0);
    };

    // --- derived rows: filter + sort + paginate
    const filtered = React.useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return items;
        return items.filter((b) => {
            const name = (b.name || "").toLowerCase();
            return name.includes(q);
        });
    }, [items, query]);

    const sorted = React.useMemo(() => {
        const normalized = filtered.map((b) => {
            const images = Array.isArray(b.images) ? b.images : [];
            let lastUpdated = 0;
            for (const img of images) {
                const tsFromDb = img?.last_updated ? new Date(img.last_updated).getTime() : 0;
                const ts = tsFromDb || parseDateFromImageFilename(img?.filename);
                if (ts > lastUpdated) lastUpdated = ts;
            }
            return {
                ...b,
                name_sort: (b.name || "").toLowerCase(),
                image_count: Number(b.image_count ?? 0),
                images_without_annotation: Number(b.images_without_annotation ?? 0),
                last_seen_date: b.last_seen_date ? new Date(b.last_seen_date).getTime() : 0,
                last_updated: lastUpdated,
            };
        });
        const sortField = orderBy === "name" ? "name_sort" : orderBy;
        return stableSort(normalized, getComparator(order, sortField));
    }, [filtered, order, orderBy]);

    const paged = React.useMemo(
        () => sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [sorted, page, rowsPerPage]
    );

    const EmptyState = () => (
        <Box
            sx={{
                py: 8,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
            }}
        >
            <Typography variant="h6">No brands yet</Typography>
            <Typography variant="body2" color="text.secondary">
                Get started by creating your first brand.
            </Typography>
            <Box mt={3} width="100%" maxWidth={480}>
                <RandomQuote />
            </Box>
        </Box>
    );

    const LoadingRows = ({ rows = 8 }) => (
        <>
            {Array.from({ length: rows }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                    {columns.map((col) => (
                        <TableCell key={col.id} align={col.align}>
                            <Skeleton variant="text" />
                        </TableCell>
                    ))}
                </TableRow>
            ))}
        </>
    );

    const canCreate = authService.isInRole?.(["ADMIN", "CREATEPROJECT"]);
    const canShowCreateButton = canCreate && items.length < brandLimit;

    return (
        <Box>
            {/* Header / Toolbar */}
            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: 3,
                    border: (t) => `1px solid ${t.palette.divider}`,
                    background: (t) =>
                        t.palette.mode === "light" ? "#fff" : t.palette.background.paper,
                }}
            >
                <Toolbar
                    disableGutters
                    sx={{
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: { xs: "stretch", sm: "center" },
                        gap: 2,
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 240 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                            Brands
                        </Typography>
                        <Stack spacing={1}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                gap={2}
                            >
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ whiteSpace: "nowrap" }}
                                >
                                    Using <strong>{formatNumber(items.length)}</strong> of{" "}
                                    <strong>{formatNumber(brandLimit)}</strong> brands
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip
                                        size="small"
                                        label={`${formatNumber(filtered.length)} result${
                                            filtered.length === 1 ? "" : "s"
                                        }`}
                                    />
                                    <FormControlLabel
                                        sx={{ ml: 1 }}
                                        control={
                                            <Switch
                                                size="small"
                                                checked={dense}
                                                onChange={(e) => setDense(e.target.checked)}
                                            />
                                        }
                                        label="Compact rows"
                                    />
                                </Stack>
                            </Stack>
                            <LinearProgress
                                variant="determinate"
                                value={usage}
                                sx={{
                                    height: 8,
                                    borderRadius: 999,
                                    bgcolor: (t) =>
                                        t.palette.mode === "light"
                                            ? t.palette.grey[200]
                                            : t.palette.grey[800],
                                }}
                            />
                        </Stack>
                    </Box>

                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                        alignItems={{ xs: "stretch", sm: "center" }}
                    >
                        <TextField
                            value={query}
                            onChange={handleSearch}
                            size="small"
                            placeholder="Search brands…"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Tooltip title="Refresh">
                            <IconButton onClick={refresh} size="small">
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Help">
              <span>
                <IconButton size="small" disabled>
                  <HelpOutlineIcon />
                </IconButton>
              </span>
                        </Tooltip>
                        {canShowCreateButton && (
                            <Box>
                                {/* Keep your existing dialog trigger */}
                                <FormDialog items={items} cb={refresh}>
                                    <IconButton color="primary" size="small">
                                        <AddCircleOutlineIcon />
                                    </IconButton>
                                </FormDialog>
                            </Box>
                        )}
                    </Stack>
                </Toolbar>
                <Divider sx={{ mt: 2 }} />
            </Paper>

            {/* Table card */}
            <Paper
                sx={{
                    width: "100%",
                    overflow: "hidden",
                    borderRadius: 3,
                }}
                elevation={0}
                variant="outlined"
            >
                <TableContainer>
                    <Table
                        stickyHeader
                        size={dense ? "small" : "medium"}
                        aria-label="brands table"
                    >
                        <TableHead>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableCell
                                        key={column.id}
                                        align={column.align}
                                        sx={{ minWidth: column.minWidth }}
                                        sortDirection={orderBy === column.id ? order : false}
                                    >
                                        {column.sortable ? (
                                            <TableSortLabel
                                                active={orderBy === column.id}
                                                direction={orderBy === column.id ? order : "asc"}
                                                onClick={() => handleRequestSort(column.id)}
                                            >
                                                {column.label}
                                            </TableSortLabel>
                                        ) : (
                                            column.label
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && <LoadingRows rows={8} />}

                            {!loading &&
                                paged.map((row) => {
                                    const lastSeen =
                                        row.last_seen_date &&
                                        new Date(row.last_seen_date).toLocaleString();
                                    const lastUpdated =
                                        row.last_updated &&
                                        new Date(row.last_updated).toLocaleString();
                                    return (
                                        <TableRow hover role="row" tabIndex={-1} key={row.id}>
                                            <TableCell>
                                                <Link to={`/brands/${row.id}`} updateTitle={updateTitle}>
                                                    {row.name}
                                                </Link>
                                            </TableCell>

                                            <TableCell align="right">
                                                <Chip
                                                    size="small"
                                                    label={formatNumber(row.image_count ?? 0)}
                                                    variant="outlined"
                                                />
                                            </TableCell>

                                            <TableCell align="right">
                                                <Chip
                                                    size="small"
                                                    color={
                                                        (row.images_without_annotation ?? 0) > 0
                                                            ? "warning"
                                                            : "success"
                                                    }
                                                    label={formatNumber(
                                                        row.images_without_annotation ?? 0
                                                    )}
                                                    variant="outlined"
                                                />
                                            </TableCell>

                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {lastUpdated || "—"}
                                                </Typography>
                                            </TableCell>

                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {lastSeen || "—"}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}

                            {!loading && filtered.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={columns.length}>
                                        <EmptyState />
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {!loading && filtered.length > 0 && (
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        component="div"
                        count={sorted.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                )}
            </Paper>

            <HelpText>
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                    These are specific channels you are subscribed to.
                </Typography>
                <Typography variant="body2">
                    You have used <strong>{formatNumber(items.length)}</strong> out of{" "}
                    <strong>{formatNumber(brandLimit)}</strong> brands.
                </Typography>
            </HelpText>

            {/* Keeps your global "blocking" loader UX */}
            <SimpleBackdrop open={loading} />
        </Box>
    );
}
